/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useEffect, useState } from 'react';
// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { createResource, useResource, type RouteResource } from '@atlassian/react-resource-router';
import {
	INCOMING_MAIL_IS_AUTH_EXPIRED_API,
	RESOURCE_TYPE_INCOMING_MAIL_IS_AUTH_EXPIRED,
	INCOMING_MAIL_IS_AUTH_EXPIRED_RESOURCE_KEY,
} from './incomingMailIsAuthExpiredResourceKey.constants.tsx';
import type {
	ResponseStatus,
	IncomingMailServerExpiryStatusGetResponse,
	IncomingMailServerExpiryStatusResourceData,
	UseIncomingMailExpiryStatus,
} from './IncomingMailServerExpiryStatusGetResponse.types.tsx';

const fetchGetIncomingMailServerExpiryStatus = async () =>
	performGetRequest<IncomingMailServerExpiryStatusGetResponse>(INCOMING_MAIL_IS_AUTH_EXPIRED_API);

export const DEFAULT_RESPONSE: IncomingMailServerExpiryStatusGetResponse = {
	isAdmin: false,
	showBanner: true,
	OauthTypes: ['GMAIL_OAUTH'],
};

export const incomingMailServerExpiryStatusResource: RouteResource<IncomingMailServerExpiryStatusResourceData> =
	createResource<IncomingMailServerExpiryStatusResourceData>({
		type: RESOURCE_TYPE_INCOMING_MAIL_IS_AUTH_EXPIRED,
		getKey: () => INCOMING_MAIL_IS_AUTH_EXPIRED_RESOURCE_KEY,
		getData: async (): Promise<IncomingMailServerExpiryStatusResourceData> => {
			let incomingMailServerExpiryStatus: IncomingMailServerExpiryStatusGetResponse | null = null;

			try {
				incomingMailServerExpiryStatus = await fetchGetIncomingMailServerExpiryStatus();

				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (error.statusCode === 404) {
					fireErrorAnalytics({
						error,
						meta: {
							id: 'incomingMailServerExpiryStatus',
							packageName: 'jiraRouterResourcesIncomingMailExpiryStatus',
							teamName: 'thor',
						},
						sendToPrivacyUnsafeSplunk: true,
					});
				}
			}
			return {
				incomingMailServerExpiryStatus: incomingMailServerExpiryStatus ?? DEFAULT_RESPONSE,
			};
		},
		maxAge: 60 * 60 * 1000, // 60 minutes
		isCritical: true,
	});

export const useIncomingMailExpiryStatus = (): UseIncomingMailExpiryStatus => {
	const {
		data,
		loading: loadingGetResource,
		error: errorGetResource,
	} = useResource<IncomingMailServerExpiryStatusResourceData>(
		incomingMailServerExpiryStatusResource,
	);

	const [incomingMailServerExpiryStatus, setIncomingMailServerExpiryStatus] =
		useState<IncomingMailServerExpiryStatusGetResponse>(() => {
			if (data?.incomingMailServerExpiryStatus) {
				return data.incomingMailServerExpiryStatus;
			}
			return DEFAULT_RESPONSE;
		});
	const [responseStatusGet, setResponseStatusGet] = useState<ResponseStatus>('not started');

	useEffect(() => {
		if (loadingGetResource) {
			// oxlint-disable-next-line react/react-compiler
			setResponseStatusGet('loading');
			return;
		}

		if (errorGetResource) {
			setResponseStatusGet('error');
			return;
		}

		if (data?.incomingMailServerExpiryStatus) {
			setResponseStatusGet('success');
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
			setIncomingMailServerExpiryStatus(data.incomingMailServerExpiryStatus);
		}
	}, [loadingGetResource, errorGetResource, data?.incomingMailServerExpiryStatus]);

	return {
		responseStatus: responseStatusGet,
		state: incomingMailServerExpiryStatus,
	};
};
